import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ScoreBar, { PATTERN_ORDER, isSkippedScore } from 'features/agent-analytics/components/ScoreBar';
import BrandLoader from 'shared/components/ui/BrandLoader';
import { fmtCost } from 'features/playground/scoreMeta';
import { activityToolbarButtonClass, dataTableFocusRing } from 'features/playground/styles';
import AnswersView from 'shared/decision/AnswersView';

/**
 * AI Score on/off, next to Run all.
 *
 * Off by default. Scoring is real work (an embedding batch plus an NLI pass per
 * output), so it happens only when asked for, and the choice is remembered with
 * the rest of the playground's state.
 */
export function ScoringToggle({ enabled, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label="Compute AI Scores on run"
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      title={enabled
        ? 'AI Scores are computed for every output on each run'
        : 'Turn on to score each output when you run'}
      className={`${activityToolbarButtonClass} disabled:cursor-not-allowed disabled:opacity-50`}
    >
      <span
        aria-hidden
        className={`relative inline-block h-3.5 w-6 shrink-0 rounded-full transition-colors ${
          enabled ? 'bg-brand' : 'bg-[color:var(--border-strong)]'
        }`}
      >
        <span
          className={`absolute top-0.5 h-2.5 w-2.5 rounded-full transition-all ${
            enabled ? 'left-3 bg-ink-onBrand' : 'left-0.5 bg-[color:var(--surface-base)]'
          }`}
        />
      </span>
      AI Score
    </button>
  );
}

/**
 * Collapsible AI Score block under one output.
 *
 * The seven generation-quality metrics, computed on demand for this output. A
 * panel that was never run, or whose scoring degraded (no embeddings / NLI
 * unavailable), shows nothing rather than a wall of empty bars.
 */
function AiScoreSection({ aiScores }) {
  const [open, setOpen] = useState(false);
  if (!aiScores || !Array.isArray(aiScores.scores)) return null;

  const byPattern = new Map(aiScores.scores.map((s) => [s.pattern, s]));
  const shown = PATTERN_ORDER
    .map((p) => byPattern.get(p))
    .filter((s) => s && !isSkippedScore(s.score, s.detail));
  if (!shown.length) return null;

  const failed = shown.filter((s) => s.crossed).length;

  return (
    <div className="mt-4 border-t border-[color:var(--border-subtle)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex h-9 w-full items-center gap-1.5 rounded text-left text-[12px] font-medium text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)] ${dataTableFocusRing}`}
        aria-expanded={open}
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <span>AI Score</span>
        <span className="text-[11px] font-normal text-[color:var(--text-disabled)]">{shown.length}</span>
        {failed > 0 && (
          <span className="ml-auto text-[11px] font-medium text-[color:var(--feedback-error)]">
            {failed} failing
          </span>
        )}
      </button>
      {open && (
        <div className="flex flex-col gap-2.5 pb-1">
          {shown.map((s) => (
            <ScoreBar
              key={s.pattern}
              pattern={s.pattern}
              score={s.score}
              threshold={s.threshold}
              crossed={s.crossed}
              detail={s.detail}
            />
          ))}
          {aiScores.meta && !aiScores.meta.nli_used && (
            <p className="text-[11px] leading-snug text-[color:var(--text-disabled)]">
              Entailment unavailable: grounding uses a stricter similarity-only bar.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** One output column in the Results section, aligned under its task. */
/** Stats for a decision answer: latency · model · tokens. */
function decisionStats(r) {
  const u = r.usage || {};
  const tok = (u.input_tokens != null || u.output_tokens != null) ? `${u.input_tokens ?? '–'}/${u.output_tokens ?? '–'} tok` : null;
  return [r.latency_ms != null ? `${r.latency_ms} ms` : null, r.model || null, tok].filter(Boolean);
}

export default function OutputColumn({ panel, running }) {
  const r = panel.result;
  const isDecision = panel.mode === 'decision';
  const stats = r && r.ok && isDecision ? decisionStats(r) : r && r.ok ? [
    r.latency_ms != null ? `${r.latency_ms} ms` : null,
    (r.usage?.input != null || r.usage?.output != null) ? `${r.usage?.input ?? '–'}/${r.usage?.output ?? '–'} tok` : null,
    r.cost_usd != null ? fmtCost(r.cost_usd) : null,
  ].filter(Boolean) : [];

  return (
    <section className="flex min-w-[360px] flex-1 flex-col px-4 pb-4">
      <div className="flex h-8 items-center justify-between gap-3">
        <h3 className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[color:var(--text-primary)]">
          {panel.title || 'Untitled task'}
        </h3>
        {stats.length > 0 && (
          <span className="shrink-0 text-[11px] tabular-nums text-[color:var(--text-muted)]">{stats.join(' · ')}</span>
        )}
      </div>
      <div className="mt-2">
        {running ? (
          <div className="py-6"><BrandLoader label="Running" size="md" /></div>
        ) : !r ? (
          <p className="py-6 text-center text-[12px] text-[color:var(--text-muted)]">Not run yet</p>
        ) : r.ok && isDecision ? (
          <AnswersView questions={r.questions || null} answers={r.answers || {}} />
        ) : r.ok ? (
          <pre className="m-0 max-h-[420px] overflow-auto whitespace-pre-wrap break-words font-mono text-[12px] leading-relaxed text-[color:var(--text-primary)]">
            {r.output || '(empty response)'}
          </pre>
        ) : (
          <p role="alert" className="text-[12px] text-[color:var(--feedback-error)]">{r.error || 'Run failed'}</p>
        )}
      </div>
      {!running && r && r.ok && !isDecision && <AiScoreSection aiScores={r.ai_scores} />}
    </section>
  );
}
